<template>
  <div id="PrivacyPolicy" class="container-fluid px-0">
    <div class="main">
      <div class="page-header">
        <div class="page-title d-flex flex-column align-items-center">
          <img class="logo" src="/img/spectrum_life_logo_light.png" />
          <h2 class="brand-text mb-0">
            PrivacyPolicy
          </h2>
        </div>
      </div>
      <div class="content">
        <div class="content-card">
          <h3>Privacy Policy – Insurance Partners</h3>
          <h3 class="pt-0">Effective Date: 6th February 2025</h3>
          <p>
            Spectrum.Life (“we,” “our,” or “us”) is committed to safeguarding
            your personal data and ensuring it is processed securely and
            transparently. This Privacy Policy explains how we collect, use,
            store, and protect your personal information, and outlines your
            rights under applicable data protection laws, including the General
            Data Protection Regulation (GDPR) and the UK Data Protection Act
            2018.
          </p>
          <p>
            This policy applies to all users accessing our services, including
            clinical and wellness programmes, digital platforms, and other
            solutions provided directly by Spectrum.Life or via Insurance
            Partner portals.
          </p>
          <h3>
            About Spectrum.Life
          </h3>
          <p>
            Spectrum.Life operates as an <b>independent data controller</b>,
            determining the purposes and means of processing your data. We do
            not engage in joint controllership with Insurance Partners. Each
            party is independently responsible for its respective processing
            activities under applicable laws.
          </p>
          <p>
            We ensure our data handling is transparent, secure, and compliant
            with the highest standards of data protection.
          </p>
          <h3>
            Principles of Data Processing
          </h3>
          <p>
            We adhere to the following principles to ensure your data is handled
            responsibly:
          </p>
          <ol>
            <li>
              <b>Lawfulness, Fairness, and Transparency:</b> All processing
              activities comply with legal obligations and are conducted fairly
              and transparently.
            </li>
            <li>
              <b>Purpose Limitation:</b> Personal data is processed only for
              specified, legitimate purposes.
            </li>
            <li>
              <b>Data Minimisation:</b> We collect only the minimum data
              necessary for the intended purposes.
            </li>
            <li>
              <b>Accuracy:</b> We strive to keep personal data accurate and
              up-to-date.
            </li>
            <li>
              <b>Storage Limitation:</b> Data is retained only as long as
              necessary for its intended purpose.
            </li>
            <li>
              <b>Integrity and Confidentiality:</b> Appropriate technical and
              organisational measures protect data against unauthorised access,
              loss, or misuse.
            </li>
          </ol>
          <h3>
            Categories of Personal Data Processed and Scope of Data Sharing
          </h3>
          <p>
            The types of data we process vary depending on the services you use.
            We share data with Insurance Partners and other third parties only
            when necessary to provide and improve services, ensuring compliance
            with GDPR and maintaining transparency. Below are examples of the
            data categories and their associated purposes:
          </p>
          <div class="grid-table">
            <div
              class="grid-header"
              v-for="(header, index) in headers"
              :key="'header-' + index"
            >
              {{ header }}
            </div>
            <div
              class="grid-cell"
              v-for="(value, index) in table1"
              :key="'cell-' + index"
            >
              {{ value }}
            </div>
          </div>
          <h3>Additional Processing Activities</h3>
          <p>
            Below are additional purposes and processing activities specific to
            certain Spectrum.Life services:
          </p>
          <div class="grid-table">
            <div
              class="grid-header"
              v-for="(header, index) in headers"
              :key="'header-' + index"
            >
              {{ header }}
            </div>
            <div
              class="grid-cell"
              v-for="(value, index) in table2"
              :key="'cell-' + index"
            >
              {{ value }}
            </div>
          </div>
          <h3>Your Rights Under GDPR</h3>
          <p>You have the following rights regarding your personal data:</p>
          <ul>
            <li><b>Access:</b> Request a copy of your personal data.</li>
            <li><b>Correction:</b> Rectify inaccurate data.</li>
            <li>
              <b>Erasure:</b> Request deletion of your data in specific
              circumstances.
            </li>
            <li><b>Restriction:</b> Request limited processing.</li>
            <li>
              <b>Portability:</b> Receive your data in a structured, commonly
              used format.
            </li>
            <li>
              <b>Objection:</b> Object to processing based on legitimate
              interests.
            </li>
            <li>
              <b>Withdraw Consent:</b> Withdraw your consent at any time where
              applicable.
            </li>
          </ul>
          <p>
            To exercise your rights, contact us at
            <a href="mailto:gdprspectrumlife@spectrum.life"
              >gdprspectrumlife@spectrum.life</a
            >.
          </p>
          <h3>
            Contact and Complaints
          </h3>
          <p>
            For concerns about your data, please contact our Data Protection
            Officer:
          </p>
          <h3>
            Data Protection Officer
          </h3>
          <p>
            Spectrum.Life Wellness Limited
            <br />
            95 Merrion Square, Dublin 2, Ireland
            <br />
            <b>Email: </b
            ><a href="mailto:gdprspectrumlife@spectrum.life"
              >gdprspectrumlife@spectrum.life</a
            >
          </p>
          <p>
            You may also lodge a complaint with your local data protection
            authority, such as the UK Information Commissioner’s Office (ICO) or
            Data Protection Commissioner (DPC).
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PrivacyPolicy",
  data() {
    return {
      headers: [
        "Purpose",
        "Data Processed",
        "Legal Basis (Article 6)",
        "Legal Basis (Article 9)"
      ],
      table1: [
        "To operate and manage our websites and apps, including login, registration, and technical support",
        "Name, e-mail address / username, password, organisation",
        "6(1)(b) Performance of a contract, 6(1)(f) Legitimate Interests",
        "N/A",
        "Direct marketing communications",
        "Name, e-mail address",
        "6(1)(f) Legitimate Interests",
        "N/A",
        "To assess suitability and deliver services, including communication and support",
        "Name, e-mail address, phone number, address, organisation, gender, date of birth, demographics",
        "6(1)(a) Consent",
        "9(2)(h) Provision of clinical care",
        "To contact you for service engagement, maintain records, and verify identity",
        "Photo / image / video / voice",
        "6(1)(a) Consent",
        "9(2)(h) Provision of clinical care",
        "To support your well-being, provide tailored advice, and manage risks",
        "Presenting issue",
        "6(1)(a) Consent",
        "9(2)(h) Provision of clinical care",
        "To improve our services and demonstrate quality through anonymised feedback",
        "Anonymised customer satisfaction and feedback",
        "N/A - Fully anonymised",
        "9(2)(c) Vital interests (if applicable)",
        "To assess suitability for specialist assessments",
        "Screening data",
        "6(1)(a) Consent",
        "9(2)(h) Provision of clinical care",
        "To support your wellness and your organisation’s duty of care",
        "Required information, risk reports, discharge reports",
        "6(1)(c) Legal obligation",
        "9(2)(h) Provision of clinical care",
        "To save your life or the life of another person",
        "Emergency contact information",
        "6(1)(d) Vital interests",
        "9(2)(c) Vital interests",
        "To inform your GP of the care you received",
        "GP’s contact information",
        "6(1)(a) Consent",
        "9(2)(a) Explicit consent",
        "To provide secure access to our services through partner organisations",
        "Partner membership profile information",
        "6(1)(b) Performance of a contract",
        "N/A",
        "To protect and analyse the use of our digital infrastructure",
        "Identifying IP information, cookies",
        "6(1)(f) Legitimate Interests",
        "N/A",
        "To recommend providers, improve our services, and train AI models",
        "Tags, gender, availability",
        "6(1)(a) Consent",
        "9(2)(a) Explicit consent",
        "To enable website functionality, personalise content, and analyse usage",
        "Cookies and captured consent",
        "6(1)(a) Consent",
        "N/A",
        "To investigate complaints and improve services through analysis",
        "Communicated complaints",
        "6(1)(a) Consent",
        "9(2)(a) Explicit consent",
        "To manage billing and payments",
        "Financial information (credit card details, etc.)",
        "6(1)(b) Performance of a contract (pre-contract included)",
        "N/A",
        "Delivering clinical and wellness services",
        "Name, contact information, demographics, health data",
        "6(1)(a) Consent or 6(1)(b) Performance of a Contract",
        "9(2)(h) Healthcare delivery",
        "Authenticating user accounts",
        "Email, username, password, login records",
        "6(1)(b) Performance of a Contract",
        "N/A",
        "Analysing service utilisation",
        "Session details, anonymised interaction data",
        "6(1)(f) Legitimate Interests: Understanding user engagement. Improving service delivery.",
        "N/A",
        "Providing personalised recommendations",
        "Service usage history, preferences",
        "6(1)(f) Legitimate Interests: Customising user experience",
        "N/A",
        "Monitoring fair usage",
        "Usage metrics, service data",
        "6(1)(f) Legitimate Interests: Ensuring equitable access. Compliance with service terms.",
        "DPA 2018 Schedule 1, Part 2, Paragraph 14 (fraud prevention).",
        "Underwriting",
        "Member-level data (claims history, risk factors)",
        "6(1)(b) Performance of a contract or 6(1)(f) Legitimate Interests",
        "DPA 2018, Schedule 1, Part 2, Paragraph 20",
        "Policy administration purposes, including service messaging, invoicing, and direct settlement",
        "Contact details, service usage logs, invoicing details",
        "6(1)(f) Legitimate Interests: Running administrative operations. Maintaining accurate records.",
        "9(2)(h) Healthcare delivery (management of healthcare systems).",
        "Data analytics for service improvement",
        "Aggregated session data, anonymised feedback",
        "6(1)(f) Legitimate Interests: Improving products and services. Provision of MI to corporate clients.",
        "9(2)(h) Healthcare delivery (management of healthcare systems).",
        "Marketing suggestions based on purchases or content viewed",
        "Interaction history, preferences, purchases",
        "6(1)(a) Consent",
        "9(2)(a) Explicit consent"
      ],
      table2: [
        "Digital Front Door Access",
        "Member ID, session details, pages visited, interactions on pages, aggregated metrics",
        "6(1)(f) Legitimate Interests: service improvement, operational efficiency",
        "N/A",
        "Personalised Service Matching (Match Tech)",
        "Group Client ID, Group Policy ID, match conditions and count",
        "6(1)(f) Legitimate Interests: operational efficiency and compliance",
        "N/A",
        "Virtual Primary Care Assistant",
        "Member ID, session details, appointment details",
        "6(1)(f) Legitimate Interests: utilisation analysis, fair use enforcement",
        "9(2)(h) Healthcare delivery",
        "Service Utilisation Analysis",
        "Aggregated metrics (session duration, trends)",
        "6(1)(f) Legitimate Interests: operational reporting and efficiency",
        "N/A",
        "Healthcare Delivery and Support",
        "Clinical appointment details, consultation outcomes",
        "6(1)(b) Performance of a contract",
        "9(2)(h) Healthcare delivery"
      ]
    };
  }
};
</script>

<style lang="less">
#PrivacyPolicy {
  .main {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .page-header {
    padding: 60px 20px 20px;
    background-color: #fbfafa;
  }

  .logo {
    max-width: 350px;
    width: 100%;
    padding-bottom: 50px;
  }

  .page-title {
    h2 {
      text-align: center;
      font-size: 3.8rem;
      letter-spacing: normal;
      font-family: "PlusJakartaSansBold";
    }
  }

  .grid-table {
    min-width: 440px;
    font-size: 1.6rem;
    display: grid;
    grid-template-columns: repeat(4, minmax(10px, 1fr));
    gap: 4px;
    width: 100%;
    margin: auto;
    border: 1px solid #ddd;
    padding: 4px;
  }

  .grid-header {
    font-family: "PlusJakartaSansBold";
    font-weight: bold;
    border: 1px solid #ddd;
    background: #f4f4f4;
    padding: 10px;
    text-align: left;
  }

  .grid-cell {
    padding: 10px;
    text-align: left;
    border: 1px solid #ddd;
  }

  .content {
    flex: 1 1 100%;
    padding: 20px 20px 60px;
    background-color: #fbfafa;
  }

  .content-card {
    height: 100%;
    background: #ffffff;
    max-width: 1000px;
    padding: 40px 60px 30px;
    margin: 0 auto;
    border-radius: 16px;
    overflow-x: scroll;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: #212529;
      font-family: "PlusJakartaSansBold";
    }

    h3 {
      font-size: 2.2rem;
      letter-spacing: 0;
      padding: 32px 0 0;
      margin: 0 0 24px;
    }

    h4 {
      font-size: 1.6rem;
      letter-spacing: 0;
      padding: 0;
      margin: 0 0 4px;
    }

    p {
      color: #212529;
      padding: 0;
      margin: 0 0 16px;
      font-size: 1.6rem;
    }

    ul,
    ol {
      color: #212529;
      margin: 0 0 16px;
    }

    li {
      color: #212529;
      font-size: 1.6rem;
      margin-bottom: 8px;
    }

    b {
      font-family: "PlusJakartaSansBold";
    }

    a {
      color: #3c16f9;

      &:hover,
      &:active {
        color: #3c16f9;
      }
    }
  }
  @media only screen and (max-width: 768px) {
    .content-card {
      padding: 30px 20px;
    }
    .grid-table {
      font-size: 1.4rem;
    }
  }
  @media only screen and (max-width: 768px) {
    .grid-table {
      font-size: 1rem;
    }
  }
}
</style>
